import React from 'react';
import {
  Box,
  Button,
  Chip,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Headphones,
  MicOff,
  PowerSettingsNew,
} from '@mui/icons-material';
import { FADER_STRIP_WIDTH_PX, formatGain } from './mixerUtils';

const meterColor = (value) => {
  if (value > 0.9) return '#ef4444';
  if (value > 0.72) return '#f59e0b';
  return '#22c55e';
};

export default function FaderStrip({
  channel,
  disabled = false,
  onGainPreview,
  onGainCommit,
  onToggleMute,
  onTogglePfl,
  onToggleOnAir,
}) {
  const level = Math.min(1, Math.max(0, Number(channel.meter || 0)));

  return (
    <Box
      sx={{
        width: FADER_STRIP_WIDTH_PX,
        minWidth: FADER_STRIP_WIDTH_PX,
        height: '100%',
        minHeight: 380,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 1.25,
        borderRadius: 2,
        bgcolor: channel.onAir ? 'var(--rs-paper)' : 'var(--rs-surface)',
        border: '1px solid',
        borderColor: channel.pfl
          ? 'var(--rs-live-console-border-active)'
          : channel.onAir
            ? 'var(--rs-live-onair-border)'
            : 'var(--rs-border)',
        boxShadow: channel.pfl ? 'inset 0 0 0 1px var(--rs-live-console-glow)' : 'none',
      }}
    >
      <Tooltip title={channel.name}>
        <Typography
          variant="caption"
          noWrap
          sx={{ width: '100%', textAlign: 'center', fontWeight: 700, color: 'text.primary' }}
        >
          {channel.name}
        </Typography>
      </Tooltip>
      <Chip
        size="small"
        label={channel.role}
        sx={{
          mt: 0.5,
          height: 18,
          fontSize: '0.58rem',
          textTransform: 'uppercase',
          bgcolor: channel.role === 'program' ? 'var(--rs-live-chip-program-bg)' : 'var(--rs-live-chip-role-bg)',
          color: channel.role === 'program' ? 'var(--rs-live-chip-program-fg)' : 'var(--rs-live-chip-role-fg)',
          '& .MuiChip-label': { color: 'inherit', px: 0.75 },
        }}
      />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 210, my: 1.25, gap: 1.25 }}>
        <Box
          aria-label={`${channel.name} level`}
          sx={{
            position: 'relative',
            width: 10,
            height: '100%',
            bgcolor: 'var(--rs-surface)',
            borderRadius: 99,
            overflow: 'hidden',
            border: '1px solid var(--rs-border)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: `${level * 100}%`,
              bgcolor: meterColor(level),
              boxShadow: `0 0 10px ${meterColor(level)}`,
              transition: 'height 80ms linear',
            }}
          />
        </Box>
        <Slider
          orientation="vertical"
          min={0}
          max={1}
          step={0.01}
          value={channel.gain}
          disabled={disabled}
          onChange={(_event, value) => onGainPreview(channel.id, Number(value))}
          onChangeCommitted={(_event, value) => onGainCommit(channel.id, Number(value))}
          aria-label={`${channel.name} fader`}
          sx={{
            color: channel.muted ? 'var(--rs-muted)' : 'var(--rs-live-fader)',
            '& .MuiSlider-thumb': { width: 26, height: 14, borderRadius: 1 },
            '& .MuiSlider-rail': { opacity: 1, bgcolor: 'var(--rs-meter-idle)' },
            '& .MuiSlider-track': { bgcolor: 'currentColor' },
          }}
        />
      </Box>

      <Typography className="tabular-nums" variant="caption" sx={{ mb: 0.75, color: 'text.secondary' }}>
        {formatGain(channel.gain)}
      </Typography>

      <Stack spacing={0.5} sx={{ width: '100%' }}>
        <Tooltip title="Silence this channel on the broadcast mix. Room peers can still hear via LiveKit.">
          <span>
            <Button
              size="small"
              fullWidth
              variant={channel.muted ? 'contained' : 'outlined'}
              color={channel.muted ? 'error' : 'inherit'}
              disabled={disabled}
              startIcon={<MicOff sx={{ fontSize: '14px !important' }} />}
              onClick={() => onToggleMute(channel)}
              sx={{ minWidth: 0, px: 0.5, fontSize: '0.65rem' }}
            >
              Mute
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Cue = headphones only. Does not change what goes to air.">
          <span>
            <Button
              size="small"
              fullWidth
              variant={channel.pfl ? 'contained' : 'outlined'}
              color={channel.pfl ? 'info' : 'inherit'}
              disabled={disabled}
              startIcon={<Headphones sx={{ fontSize: '14px !important' }} />}
              onClick={() => onTogglePfl(channel)}
              sx={{ minWidth: 0, px: 0.5, fontSize: '0.65rem' }}
            >
              Cue
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Off air keeps this strip out of the broadcast mix while staying in the room.">
          <span>
            <Button
              size="small"
              fullWidth
              variant={channel.onAir ? 'contained' : 'outlined'}
              color={channel.onAir ? 'success' : 'inherit'}
              disabled={disabled}
              startIcon={<PowerSettingsNew sx={{ fontSize: '14px !important' }} />}
              onClick={() => onToggleOnAir(channel)}
              sx={{ minWidth: 0, px: 0.5, fontSize: '0.62rem' }}
            >
              {channel.onAir ? 'On air' : 'Off air'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}
